import { useState, useEffect, useRef } from "react";
import { APP_VERSION } from "../../shared/version";

interface UpdateBannerViewProps {
  readonly version: string;
  readonly changes?: readonly string[];
  readonly onUpdate: () => void;
}

export function UpdateBannerView({ version, changes = [], onUpdate }: UpdateBannerViewProps) {
  return (
    <div className="fixed bottom-20 md:bottom-6 left-1/2 -translate-x-1/2 z-[100] animate-slide-up w-[calc(100%-2rem)] max-w-sm md:w-auto">
      <div className="flex flex-col md:flex-row items-stretch md:items-center gap-2 md:gap-3 bg-surface border border-border rounded-xl shadow-xl px-4 py-3">
        <div className="flex flex-col gap-0.5">
          <p className="text-sm text-ink font-medium">
            Version {version} disponible
          </p>
          {changes.length > 0 ? (
            changes.length === 1 ? (
              <p className="text-xs text-ink-muted">{changes[0]}</p>
            ) : (
              <ul className="flex flex-col gap-0.5 mt-0.5">
                {changes.map((change, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-xs text-ink-muted">
                    <span className="shrink-0 mt-1.5 w-1 h-1 rounded-full bg-accent/40" />
                    {change}
                  </li>
                ))}
              </ul>
            )
          ) : (
            <p className="text-xs text-ink-muted">Améliorations et optimisations générales</p>
          )}
        </div>
        <button
          type="button"
          onClick={onUpdate}
          className="shrink-0 px-3 py-1.5 text-xs font-semibold bg-accent text-white rounded-lg hover:opacity-80 transition-opacity"
        >
          Mettre à jour
        </button>
      </div>
    </div>
  );
}

export function UpdateBanner() {
  const [newVersion, setNewVersion] = useState<string | null>(null);
  const [changes, setChanges] = useState<string[]>([]);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "SW_UPDATED") {
        setNewVersion(event.data.version ?? "nouvelle");
        if (Array.isArray(event.data.changes) && event.data.changes.length > 0) {
          setChanges(event.data.changes);
        }
      }
    };

    navigator.serviceWorker.addEventListener("message", handleMessage);

    navigator.serviceWorker.ready.then((registration) => {
      const requestVersion = (worker: ServiceWorker) => {
        worker.postMessage({ type: "GET_VERSION" });
        setTimeout(() => {
          setNewVersion((v) => v ?? "nouvelle");
        }, 2000);
      };

      if (registration.waiting) {
        requestVersion(registration.waiting);
      }

      registration.addEventListener("updatefound", () => {
        const newWorker = registration.installing;
        if (!newWorker) return;
        newWorker.addEventListener("statechange", () => {
          if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
            requestVersion(newWorker);
          }
        });
      });
    });

    return () => {
      navigator.serviceWorker.removeEventListener("message", handleMessage);
    };
  }, []);

  // Poll /api/health pour détecter les mises à jour même sans interaction
  const pollingRef = useRef(false);
  useEffect(() => {
    if (APP_VERSION === "dev" || newVersion) return;
    pollingRef.current = true;

    const checkVersion = async () => {
      try {
        const res = await fetch("/api/health");
        if (!res.ok) return;
        const data = await res.json();
        if (data.version && data.version !== APP_VERSION && pollingRef.current) {
          setNewVersion(data.version);
        }
      } catch { /* silencieux */ }
    };

    const poll = setInterval(checkVersion, 3 * 60 * 1000);
    const initial = setTimeout(checkVersion, 30 * 1000);
    return () => {
      pollingRef.current = false;
      clearInterval(poll);
      clearTimeout(initial);
    };
  }, [newVersion]);

  if (!newVersion) return null;

  return (
    <UpdateBannerView
      version={newVersion}
      changes={changes}
      onUpdate={() => window.location.reload()}
    />
  );
}
